import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Trophy, Loader2, Play, X, Lock, Clock, Award, Star } from 'lucide-react';
import { useLeaderboard, type LeaderboardItem } from '../../hooks/useLeaderboard';
import ProfileCompareView from './ProfileCompareView';
import { useShuddhoshikhaContext } from '../../hooks/useShuddhoshikhaContext';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';

interface LeaderboardViewProps {
  isDarkMode?: boolean;
  onViewUserProfile?: (userId: number) => void;
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// Map ranking numbers to Bengali digits
const toBengaliNumber = (num: number | string): string => {
  if (num === null || num === undefined) return '০';
  const englishToBengali: Record<string, string> = {
    '0': '০', '1': '১', '2': '২', '3': '৩', '4': '৪',
    '5': '৫', '6': '৬', '7': '৭', '8': '৮', '9': '৯'
  };
  return num.toString().split('').map(digit => englishToBengali[digit] || digit).join('');
};

// Assign premium color gradients based on Rank on the podium
const getPodiumGradient = (rank: number): string => {
  if (rank === 1) return 'from-[#00CBA9] to-[#00A3FF]'; // Rank 1
  if (rank === 2) return 'from-purple-500 to-pink-500'; // Rank 2
  return 'from-orange-500 to-rose-500'; // Rank 3
};

// Assign premium color gradients based on Rank for the lower rank list items
const getListGradient = (rank: number): string => {
  const gradients = [
    'from-blue-500 to-indigo-500',
    'from-teal-500 to-emerald-500',
    'from-cyan-500 to-blue-500',
    'from-rose-500 to-red-500',
    'from-violet-500 to-fuchsia-500',
    'from-amber-500 to-orange-500',
    'from-emerald-500 to-teal-600'
  ];
  const index = (rank - 4) % gradients.length;
  return gradients[index < 0 ? 0 : index];
};

// Dynamic formatting for points
const formatPoints = (points: number): string => {
  if (points >= 1000) {
    const formatted = (points / 1000).toFixed(2);
    const clean = parseFloat(formatted).toString();
    return toBengaliNumber(clean) + 'k';
  }
  return toBengaliNumber(points);
};

// League definitions
interface League {
  id: string;
  name: string;
  threshold: number;
  color: string;
  icon: string;
}

const LEAGUES: League[] = [
  { id: 'iron', name: 'আইরন লীগ', threshold: 0, color: '#71717a', icon: 'iron' },
  { id: 'bronze', name: 'ব্রোঞ্জ লীগ', threshold: 100, color: '#b45309', icon: 'bronze' },
  { id: 'silver', name: 'সিলভার লীগ', threshold: 500, color: '#64748b', icon: 'silver' },
  { id: 'gold', name: 'গোল্ড লীগ', threshold: 1000, color: '#eab308', icon: 'gold' },
  { id: 'diamond', name: 'ডায়মন্ড লীগ', threshold: 3000, color: '#a855f7', icon: 'diamond' },
  { id: 'infinity', name: 'ইনফিনিটি লীগ', threshold: 5000, color: '#3b82f6', icon: 'infinity' },
];

export default function LeaderboardView({ isDarkMode, onViewUserProfile }: LeaderboardViewProps) {
  const selectedLevel = 'all';
  const selectedGroup = 'all';
  const [selectedLeague, setSelectedLeague] = useState<string>('iron');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // Challenge Modal States
  const [challengeUserId, setChallengeUserId] = useState<number | null>(null);
  const [challengeUserName, setChallengeUserName] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  const { subjects } = useShuddhoshikhaContext();

  // Fetch rankings for current league, level & group
  const { data, loading, error } = useLeaderboard(selectedLevel, selectedGroup, selectedLeague);

  const rankings = data?.rankings || [];
  const userPoints = data?.user_points || 0;

  // Split into Top 3 and Rank 4+
  const topThree = rankings.slice(0, 3);
  const rankFourPlus = rankings.slice(3);

  // Countdown timer for Infinity League reset
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      // End of current month
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      const diff = endOfMonth.getTime() - now.getTime();
      
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft({ days, hours, minutes, seconds });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSendChallenge = async () => {
    if (!challengeUserId || !selectedSubjectId || !selectedChapterId) {
      alert('দয়া করে বিষয় ও অধ্যায় নির্বাচন করুন।');
      return;
    }

    try {
      setSendingInvite(true);
      const shAuth = (window as any).shAuth;
      const reactObj = (window as any).sh_react_object;
      const ajaxUrl = normalizeAjaxUrl(shAuth?.ajax_url || reactObj?.ajax_url || '/wp-admin/admin-ajax.php');
      const nonce = reactObj?.nonce || shAuth?.nonce || '';

      const formData = new FormData();
      formData.append('action', 'sh_send_battle_invitation');
      formData.append('friend_id', String(challengeUserId));
      formData.append('subject_id', selectedSubjectId);
      formData.append('chapter_id', selectedChapterId);
      formData.append('nonce', nonce);

      const response = await fetch(ajaxUrl, {
        method: 'POST',
        body: formData,
      });

      const res = await response.json();
      if (res.success && res.data?.room_id) {
        window.location.href = `${window.location.origin}${window.location.pathname}?battle=${res.data.room_id}&subject=${selectedSubjectId}&chapter=${selectedChapterId}`;
      } else {
        alert(res.data || 'আমন্ত্রণ পাঠাতে ব্যর্থ হয়েছে।');
      }
    } catch {
      alert('নেটওয়ার্ক সংযোগ ব্যর্থ হয়েছে।');
    } finally {
      setSendingInvite(false);
    }
  };

  // Reorder Top 3 for Podium display: [Rank 2, Rank 1, Rank 3]
  const orderedPodium = [];
  if (topThree.length >= 2) {
    orderedPodium.push(topThree[1]); // Rank 2 (Left)
  }
  if (topThree.length >= 1) {
    orderedPodium.push(topThree[0]); // Rank 1 (Middle)
  }
  if (topThree.length >= 3) {
    orderedPodium.push(topThree[2]); // Rank 3 (Right)
  }

  // Render Premium League Badge Icons matching the screenshots
  const renderLeagueBadge = (type: string, sizeClass = 'w-12 h-12') => {
    switch (type) {
      case 'iron':
        return (
          <svg className={sizeClass} viewBox="0 0 100 100" fill="none">
            <defs>
              <linearGradient id="ironGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4B5563" />
                <stop offset="50%" stopColor="#374151" />
                <stop offset="100%" stopColor="#1F2937" />
              </linearGradient>
              <linearGradient id="ironBorder" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#9CA3AF" />
                <stop offset="100%" stopColor="#4B5563" />
              </linearGradient>
            </defs>
            <polygon points="50,5 92,26 92,74 50,95 8,74 8,26" fill="url(#ironGrad)" stroke="url(#ironBorder)" strokeWidth="5" strokeLinejoin="round" />
            <polygon points="50,16 82,32 82,68 50,84 18,68 18,32" fill="#1F2937" opacity="0.8" stroke="#4B5563" strokeWidth="2" />
            <polygon points="50,30 68,40 68,60 50,70 32,60 32,40" fill="#374151" />
          </svg>
        );
      case 'bronze':
        return (
          <svg className={sizeClass} viewBox="0 0 100 100" fill="none">
            <defs>
              <linearGradient id="bronzeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A75A3B" />
                <stop offset="50%" stopColor="#78350F" />
                <stop offset="100%" stopColor="#451A03" />
              </linearGradient>
              <linearGradient id="bronzeBorder" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#D97706" />
                <stop offset="100%" stopColor="#78350F" />
              </linearGradient>
            </defs>
            <polygon points="50,6 92,30 76,86 24,86 8,30" fill="url(#bronzeGrad)" stroke="url(#bronzeBorder)" strokeWidth="6" strokeLinejoin="round" />
            <polygon points="50,18 82,36 70,76 30,76 18,36" fill="#451A03" opacity="0.9" />
            <polygon points="50,28 66,40 60,66 40,66 34,40" fill="#B45309" stroke="#F59E0B" strokeWidth="2" />
            <circle cx="43" cy="46" r="3.5" fill="#FBBF24" />
            <circle cx="57" cy="46" r="3.5" fill="#FBBF24" />
          </svg>
        );
      case 'silver':
        return (
          <svg className={sizeClass} viewBox="0 0 100 100" fill="none">
            <defs>
              <linearGradient id="silverGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E2E8F0" />
                <stop offset="50%" stopColor="#94A3B8" />
                <stop offset="100%" stopColor="#475569" />
              </linearGradient>
              <linearGradient id="silverBorder" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="100%" stopColor="#64748B" />
              </linearGradient>
            </defs>
            <polygon points="50,6 92,30 76,86 24,86 8,30" fill="url(#silverGrad)" stroke="url(#silverBorder)" strokeWidth="6" strokeLinejoin="round" />
            <polygon points="50,18 82,36 70,76 30,76 18,36" fill="#334155" opacity="0.9" />
            <polygon points="50,28 66,40 60,66 40,66 34,40" fill="#64748B" stroke="#94A3B8" strokeWidth="2" />
            <circle cx="43" cy="46" r="3.5" fill="#E2E8F0" />
            <circle cx="57" cy="46" r="3.5" fill="#E2E8F0" />
          </svg>
        );
      case 'gold':
        return (
          <svg className={sizeClass} viewBox="0 0 100 100" fill="none">
            <defs>
              <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FDE047" />
                <stop offset="50%" stopColor="#EAB308" />
                <stop offset="100%" stopColor="#854D0E" />
              </linearGradient>
              <linearGradient id="goldBorder" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FEF08A" />
                <stop offset="100%" stopColor="#CA8A04" />
              </linearGradient>
            </defs>
            <polygon points="50,6 92,30 76,86 24,86 8,30" fill="url(#goldGrad)" stroke="url(#goldBorder)" strokeWidth="6" strokeLinejoin="round" />
            <polygon points="50,18 82,36 70,76 30,76 18,36" fill="#713F12" opacity="0.9" />
            <polygon points="50,28 66,40 60,66 40,66 34,40" fill="#CA8A04" stroke="#FDE047" strokeWidth="2" />
            <circle cx="43" cy="46" r="3.5" fill="#FEF08A" />
            <circle cx="57" cy="46" r="3.5" fill="#FEF08A" />
          </svg>
        );
      case 'diamond':
        return (
          <svg className={sizeClass} viewBox="0 0 100 100" fill="none">
            <defs>
              <linearGradient id="diamondGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#C084FC" />
                <stop offset="50%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#4C1D95" />
              </linearGradient>
              <linearGradient id="diamondBorder" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#E9D5FF" />
                <stop offset="100%" stopColor="#7E22CE" />
              </linearGradient>
            </defs>
            <path d="M10,35 C5,20 2,45 8,65 C12,55 18,50 25,48" stroke="#C084FC" strokeWidth="3" fill="none" />
            <path d="M90,35 C95,20 98,45 92,65 C88,55 82,50 75,48" stroke="#C084FC" strokeWidth="3" fill="none" />
            <polygon points="50,6 88,27 88,73 50,94 12,73 12,27" fill="url(#diamondGrad)" stroke="url(#diamondBorder)" strokeWidth="5" strokeLinejoin="round" />
            <polygon points="50,16 78,32 78,68 50,84 22,68 22,32" fill="#2E1065" opacity="0.9" />
            <polygon points="50,30 65,40 60,64 40,64 35,40" fill="#7E22CE" stroke="#D8B4FE" strokeWidth="2" />
            <polygon points="50,32 54,42 64,42 56,48 59,58 50,52 41,58 44,48 36,42 46,42" fill="#FFFFFF" opacity="0.9" />
          </svg>
        );
      case 'infinity':
      default:
        return (
          <svg className={sizeClass} viewBox="0 0 100 100" fill="none">
            <defs>
              <linearGradient id="infinityGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FF6B6B" />
                <stop offset="30%" stopColor="#F59E0B" />
                <stop offset="60%" stopColor="#10B981" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
              <linearGradient id="infinityBorder" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFF" />
                <stop offset="50%" stopColor="#F43F5E" />
                <stop offset="100%" stopColor="#881337" />
              </linearGradient>
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>
            <path d="M12,24 C-2,10 -6,45 8,70 C15,60 22,52 30,50 M8,45 C-1,35 1,55 12,65" stroke="#F43F5E" strokeWidth="4.5" fill="none" strokeLinecap="round" />
            <path d="M88,24 C102,10 106,45 92,70 C85,60 78,52 70,50 M92,45 C101,35 99,55 88,65" stroke="#F43F5E" strokeWidth="4.5" fill="none" strokeLinecap="round" />
            <polygon points="50,6 88,27 88,73 50,94 12,73 12,27" fill="url(#infinityGrad)" stroke="url(#infinityBorder)" strokeWidth="6" strokeLinejoin="round" filter="url(#glow)" />
            <polygon points="50,16 78,32 78,68 50,84 22,68 22,32" fill="#4C0519" opacity="0.9" />
            <path d="M38,50 C38,44 43,40 47,44 L53,56 C57,60 62,56 62,50 C62,44 57,40 53,44 L47,56 C43,60 38,56 38,50 Z" stroke="#FFF" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M50,22 L52,28 L58,30 L52,32 L50,38 L48,32 L42,30 L48,28 Z" fill="#FFF" />
          </svg>
        );
    }
  };

  if (selectedUserId !== null) {
    return (
      <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-6 shadow-sm border border-gray-150 dark:border-white/5">
        <ProfileCompareView
          targetUserId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          isDarkMode={isDarkMode}
          onChallengeFriend={(friendId, friendName) => {
            setChallengeUserId(friendId);
            setChallengeUserName(friendName);
            setSelectedUserId(null);
          }}
        />
      </div>
    );
  }

  const activeLeagueObj = LEAGUES.find(l => l.id === selectedLeague) || LEAGUES[0];
  const isLeagueLocked = userPoints < activeLeagueObj.threshold;

  return (
    <>
      <motion.div 
        variants={itemVariants} 
        className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-white/5 space-y-6"
      >
        {/* Title Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              শিক্ষার্থী (লিডারবোর্ড)
            </h3>
            <p className="font-bangla text-xs text-gray-500 dark:text-[#A0A5AD] mt-0.5">কুইজ পারফরম্যান্সের ভিত্তিতে শীর্ষস্থান অধিকারী শিক্ষার্থীরা</p>
          </div>
          <span className="self-start sm:self-center font-bangla text-[10px] md:text-xs text-gray-400 dark:text-[#A0A5AD] bg-gray-50 dark:bg-white/5 px-3 py-1 rounded-full border border-gray-100 dark:border-white/5">
            আজকের আপডেট
          </span>
        </div>

        {/* 6-Tier Gamified League Headers */}
        <div className="bg-[#F8FAFC] dark:bg-[#1C1C1E]/50 border border-gray-100 dark:border-white/5 rounded-3xl p-4.5">
          <div className="grid grid-cols-6 gap-2 text-center items-center">
            {LEAGUES.map((league) => {
              const isLockedForUser = userPoints < league.threshold;
              const isSelected = selectedLeague === league.id;
              
              return (
                <button
                  key={league.id}
                  onClick={() => setSelectedLeague(league.id)}
                  className={`flex flex-col items-center p-2.5 rounded-2xl transition-all cursor-pointer relative ${
                    isSelected 
                      ? 'bg-white dark:bg-white/5 shadow-md shadow-gray-100/30 scale-105 border border-gray-100 dark:border-white/5' 
                      : 'hover:bg-slate-50 dark:hover:bg-white/3 border border-transparent'
                  }`}
                >
                  <div className="relative">
                    {renderLeagueBadge(league.id, isSelected ? 'w-10 h-10 md:w-12 md:h-12' : 'w-8 h-8 md:w-9 md:h-9')}
                    {isLockedForUser && (
                      <div className="absolute -bottom-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5 border border-white dark:border-[#1E1E1E]">
                        <Lock className="w-2.5 h-2.5" />
                      </div>
                    )}
                  </div>
                  <span className={`font-bangla text-[9px] mt-2 font-bold ${
                    isSelected 
                      ? 'text-slate-800 dark:text-white font-extrabold' 
                      : 'text-gray-400 dark:text-[#A0A5AD]'
                  }`}>
                    {league.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected League Stats & Info */}
        <div className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 rounded-3xl p-5 space-y-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              {renderLeagueBadge(activeLeagueObj.id, 'w-14 h-14 md:w-16 md:h-16')}
              <div className="space-y-0.5">
                <h4 className="font-bangla text-base font-extrabold text-slate-800 dark:text-white">{activeLeagueObj.name}</h4>
                <p className="font-bangla text-[10px] text-gray-400 dark:text-zinc-550">
                  {activeLeagueObj.threshold > 0 
                    ? `আনলক করতে কুইজ থেকে ন্যূনতম ${toBengaliNumber(activeLeagueObj.threshold)} পয়েন্ট প্রয়োজন`
                    : 'সবার জন্য উন্মুক্ত বেস লীগ'}
                </p>
              </div>
            </div>

            {/* Current user point indicator */}
            <div className="bg-white dark:bg-white/5 px-4.5 py-2.5 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm text-right">
              <span className="font-bangla text-[10px] text-gray-400 dark:text-zinc-550 block mb-0.5">তোমার মোট কুইজ পয়েন্ট</span>
              <span className="font-bangla text-sm font-black text-[#00CBA9] flex items-center gap-1.5 justify-end">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                {toBengaliNumber(userPoints.toFixed(1))} পয়েন্ট
              </span>
            </div>
          </div>

          {/* Progress Bar Section matching mockup design */}
          {(() => {
            const activeLeagueIndex = LEAGUES.findIndex(l => l.id === activeLeagueObj.id);
            const nextLeagueObj = LEAGUES[activeLeagueIndex + 1];
            
            const minVal = activeLeagueObj.threshold;
            const maxVal = nextLeagueObj ? nextLeagueObj.threshold : Math.max(5000, userPoints, 7500);
            
            const percent = Math.min(100, Math.max(0, ((userPoints - minVal) / (maxVal - minVal)) * 100));
            
            return (
              <div className="pt-3 pb-2 text-left md:ml-[78px] md:mr-[80px]">
                {/* Track */}
                <div className="relative w-full h-3.5 bg-gray-250 dark:bg-zinc-850 rounded-full">
                  {/* Filled track */}
                  <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(245,158,11,0.25)]"
                    style={{ width: `${percent}%` }}
                  />

                  {/* Floating Star Pill Indicator */}
                  <div 
                    className="absolute -top-3.5 flex items-center gap-1.5 px-3 py-1 bg-[#1C1917] dark:bg-zinc-950 text-white rounded-full border border-amber-400 shadow-lg text-[10px] font-black transition-all duration-700 select-none cursor-default"
                    style={{ 
                      left: `${percent}%`,
                      transform: 'translateX(-50%)'
                    }}
                  >
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    <span className="font-english font-black">{toBengaliNumber(Math.round(userPoints))}</span>
                  </div>
                </div>

                {/* Min / Max Labels */}
                <div className="flex justify-between mt-2 px-1">
                  <span className="font-bangla text-[10px] text-gray-400 dark:text-zinc-550 font-bold">
                    {toBengaliNumber(minVal)}
                  </span>
                  <span className="font-bangla text-[10px] text-gray-400 dark:text-zinc-550 font-bold">
                    {toBengaliNumber(maxVal)}
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Locked State Warning Banner */}
          {isLeagueLocked && (
            <div className="py-3 px-4.5 bg-rose-500/10 border border-rose-500/25 text-rose-500 dark:text-rose-400 font-bangla text-xs font-bold rounded-2xl flex items-center gap-2">
              <Lock className="w-4 h-4 shrink-0" />
              <span>এই লীগ আনলক করতে পূর্ববর্তী লীগ গুলো কমপ্লিট করো</span>
            </div>
          )}

          {/* Infinity League / Diamond League Countdown Timer */}
          {(selectedLeague === 'diamond' || selectedLeague === 'infinity') && (
            <div className="py-3 px-4.5 bg-indigo-500/10 dark:bg-indigo-500/5 border border-indigo-500/25 text-indigo-600 dark:text-[#8AB4F8] font-bangla text-xs font-bold rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-500 animate-pulse" />
                <span>ইনফিনিটি লীগ শুরু হতে আর বাকি</span>
              </div>
              <div className="flex items-center gap-1.5 font-english font-black text-sm">
                <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-lg">{toBengaliNumber(timeLeft.days)}d</span>
                <span>:</span>
                <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-lg">{toBengaliNumber(timeLeft.hours)}h</span>
                <span>:</span>
                <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-lg">{toBengaliNumber(timeLeft.minutes)}m</span>
                <span>:</span>
                <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-lg">{toBengaliNumber(timeLeft.seconds)}s</span>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        {loading ? (
          <div className="space-y-4 py-8 animate-pulse text-center">
            <div className="h-32 bg-gray-100 dark:bg-white/5 rounded-2xl w-full max-w-sm mx-auto mb-6"></div>
            <div className="space-y-2 max-w-md mx-auto">
              <div className="h-10 bg-gray-100 dark:bg-white/5 rounded-xl"></div>
              <div className="h-10 bg-gray-100 dark:bg-white/5 rounded-xl"></div>
              <div className="h-10 bg-gray-100 dark:bg-white/5 rounded-xl"></div>
            </div>
          </div>
        ) : error ? (
          <div className="py-8 text-center text-rose-500 dark:text-rose-400 font-bangla text-xs border border-rose-200/20 rounded-2xl bg-rose-50/5">
            র‌্যাংকিং লোড করতে ব্যর্থ হয়েছে। দয়া করে আবার চেষ্টা করুন।
          </div>
        ) : rankings.length === 0 ? (
          <div className="py-12 text-center text-gray-400 dark:text-[#A0A5AD]/60 font-bangla text-xs">
            এই ফিল্টারে কোনো কুইজ সম্পন্নকারী শিক্ষার্থী নেই।
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div 
              key={`${selectedLevel}-${selectedGroup}-${selectedLeague}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* Top 3 Podium */}
              {topThree.length > 0 && (
                <div className="flex items-end justify-center gap-3 sm:gap-6 md:gap-12 py-8 bg-[#F8FAFC] dark:bg-[#1E1E1E]/20 rounded-2xl border border-gray-100/50 dark:border-white/5">
                  {orderedPodium.map((student) => {
                    const isRank1 = student.rank === 1;
                    const avatarSize = isRank1 ? 'w-18 h-18 sm:w-22 sm:h-22 text-lg sm:text-xl' : 'w-14 h-14 sm:w-18 sm:h-18 text-base sm:text-lg';
                    const containerTranslate = isRank1 ? '-translate-y-3 sm:-translate-y-4' : '';
                    const badgeSize = isRank1 ? 'w-5.5 h-5.5 sm:w-6 sm:h-6 text-xs sm:text-sm' : 'w-5 h-5 text-[10px] sm:text-xs';
                    const nameSize = isRank1 ? 'text-sm sm:text-base font-bold' : 'text-xs sm:text-sm font-semibold';
                    
                    return (
                      <div 
                        key={student.id} 
                        className={`flex flex-col items-center text-center space-y-1 ${containerTranslate} ${
                          student.isCurrentUser ? 'scale-105' : 'cursor-pointer hover:opacity-85 transition-all'
                        }`}
                        onClick={() => {
                          if (!student.isCurrentUser) {
                            if (onViewUserProfile) {
                              onViewUserProfile(student.id);
                            } else {
                              setSelectedUserId(student.id);
                            }
                          }
                        }}
                      >
                        <div className="relative">
                          {isRank1 && (
                            <div className="absolute -top-6 sm:-top-7 left-1/2 -translate-x-1/2 text-amber-500 animate-bounce">
                              <Crown className="w-6 h-6 sm:w-7 sm:h-7 fill-current" />
                            </div>
                          )}
                          
                          <div className={`${avatarSize} rounded-full bg-gradient-to-br ${getPodiumGradient(student.rank)} flex items-center justify-center text-white font-english font-bold ring-4 ${
                            student.isCurrentUser
                              ? 'ring-[#00CBA9]/40'
                              : isRank1
                              ? 'ring-[#00CBA9]/20 dark:ring-[#8AB4F8]/20'
                              : 'ring-[#00A3FF]/20'
                          } shadow-lg`}>
                            {student.initials}
                          </div>

                          <div className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 ${badgeSize} rounded-full ${
                            student.rank === 1 ? 'bg-[#00CBA9] dark:bg-[#8AB4F8]' : 'bg-[#00A3FF]'
                          } text-white dark:text-[#121212] flex items-center justify-center font-bangla font-black shadow-sm`}>
                            {toBengaliNumber(student.rank)}
                          </div>
                        </div>

                        <div className="pt-2 max-w-[90px] sm:max-w-[130px]">
                          <p className={`font-bangla ${nameSize} ${
                            student.isCurrentUser
                              ? 'text-[#00CBA9] dark:text-[#8AB4F8] font-bold'
                              : 'text-[#1F1F1F] dark:text-[#E8EAED]'
                          } truncate`}>
                            {student.name} {student.isCurrentUser && '(তুমি)'}
                          </p>
                          <div className="flex items-center justify-center gap-1 text-[#64748b] dark:text-[#A0A5AD] mt-0.5">
                            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                            <span className="font-bangla text-[10px] sm:text-xs font-semibold">
                              {formatPoints(student.points)} পয়েন্ট
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Ranks 4+ List View */}
              {rankFourPlus.length > 0 && (
                <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                  {rankFourPlus.map((student: LeaderboardItem) => (
                    <div
                      key={student.id}
                      className={`flex items-center gap-3 sm:gap-4 p-3 rounded-2xl border transition-all ${
                        student.isCurrentUser
                          ? 'bg-[#00CBA9]/10 dark:bg-[#8AB4F8]/10 border-[#00CBA9]/25 dark:border-[#8AB4F8]/25 shadow-sm'
                          : 'bg-white dark:bg-[#1E1E1E]/30 border-gray-100 dark:border-white/5 hover:bg-[#F4F7FB] dark:hover:bg-white/5 cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!student.isCurrentUser) {
                          if (onViewUserProfile) {
                            onViewUserProfile(student.id);
                          } else {
                            setSelectedUserId(student.id);
                          }
                        }
                      }}
                    >
                      <span className="font-english font-bold text-xs sm:text-sm text-gray-400 dark:text-[#A0A5AD] w-5 text-center">
                        {toBengaliNumber(student.rank)}
                      </span>

                      <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${getListGradient(student.rank)} flex items-center justify-center text-white font-english font-semibold text-xs sm:text-sm shadow-sm`}>
                        {student.initials}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`font-bangla text-xs sm:text-sm font-semibold truncate ${
                          student.isCurrentUser 
                            ? 'text-[#00CBA9] dark:text-[#8AB4F8] font-bold' 
                            : 'text-gray-700 dark:text-[#D1D5DB]'
                        }`}>
                          {student.name} {student.isCurrentUser && '(তুমি)'}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 text-gray-400 dark:text-[#A0A5AD] font-bangla text-[10px] sm:text-xs font-medium">
                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                        <span className="font-semibold text-slate-750 dark:text-slate-200">
                          {formatPoints(student.points)} পয়েন্ট
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Infinity League "গত মাসের সেরা পারফর্মার" section */}
              {selectedLeague === 'infinity' && (
                <div className="bg-[#F4F2FF] dark:bg-purple-950/20 border border-[#E5DEFF] dark:border-white/5 rounded-3xl p-5 space-y-3.5">
                  <h4 className="font-bangla text-xs font-black text-indigo-950 dark:text-purple-300 flex items-center gap-2">
                    <Award className="w-5 h-5 text-indigo-500 fill-indigo-200" />
                    গত মাসের সেরা পারফর্মার (চ্যাম্পিয়ন্স)
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { name: 'তানভীর রহমান', points: 9280, rank: '🥇 ১ম' },
                      { name: 'নাসরিন সুলতানা', points: 8750, rank: '🥈 ২য়' },
                      { name: 'আরিফুল ইসলাম', points: 8400, rank: '🥉 ৩য়' }
                    ].map((perf, pIdx) => (
                      <div key={pIdx} className="bg-white dark:bg-[#1E1E1E]/50 p-3 rounded-2xl text-center space-y-1 shadow-sm border border-slate-100 dark:border-white/5">
                        <span className="font-bangla text-[10px] font-black text-indigo-600 block">{perf.rank}</span>
                        <p className="font-bangla text-[11px] font-extrabold text-slate-800 dark:text-slate-200 truncate">{perf.name}</p>
                        <span className="font-bangla text-[9px] text-slate-400 dark:text-zinc-550">{formatPoints(perf.points)} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        )}
      </motion.div>

      {/* Challenge Invitation Modal */}
      {challengeUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1E1E1E] rounded-3xl p-6 max-w-sm w-full space-y-4 border border-gray-100 dark:border-white/10 shadow-2xl">
            <div className="flex justify-between items-center">
              <h4 className="font-bangla text-sm font-extrabold text-[#1F1F1F] dark:text-[#E8EAED]">
                {challengeUserName}-কে কুইজ চ্যালেঞ্জ করুন
              </h4>
              <button 
                onClick={() => setChallengeUserId(null)} 
                className="p-1 rounded-lg hover:bg-gray-150 dark:hover:bg-white/5 text-gray-500 border-none cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="space-y-1">
                <label className="font-bangla font-semibold text-gray-500 dark:text-gray-400">বিষয় নির্বাচন করুন</label>
                <select
                  value={selectedSubjectId}
                  onChange={(e) => {
                    setSelectedSubjectId(e.target.value);
                    setSelectedChapterId('');
                  }}
                  className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent dark:text-white"
                >
                  <option value="" className="dark:bg-[#1E1E1E]">নির্বাচন করুন</option>
                  {subjects?.map((sub: any) => (
                    <option key={sub.id} value={sub.id} className="dark:bg-[#1E1E1E]">{sub.name}</option>
                  ))}
                </select>
              </div>

              {selectedSubjectId && (
                <div className="space-y-1">
                  <label className="font-bangla font-semibold text-gray-500 dark:text-gray-400">অধ্যায় নির্বাচন করুন</label>
                  <select
                    value={selectedChapterId}
                    onChange={(e) => setSelectedChapterId(e.target.value)}
                    className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent dark:text-white"
                  >
                    <option value="" className="dark:bg-[#1E1E1E]">নির্বাচন করুন</option>
                    {subjects?.find((s: any) => s.id === selectedSubjectId)?.chapters?.map((chap: any) => (
                      <option key={chap.id} value={chap.id} className="dark:bg-[#1E1E1E]">{chap.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <button
              onClick={handleSendChallenge}
              disabled={sendingInvite || !selectedSubjectId || !selectedChapterId}
              className="w-full py-3.5 rounded-2xl bg-[#00CBA9] hover:bg-[#00B097] text-white font-bangla font-bold text-xs border-none cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/10 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingInvite ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>আমন্ত্রণ পাঠানো হচ্ছে...</span>
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 fill-current" />
                  <span>আমন্ত্রণ পাঠান</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
